/**
 * API Service
 * Handles all backend API communication including SSE streaming
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Health check endpoint
 */
export async function checkHealth() {
  const response = await fetch(`${API_BASE_URL}/health`);
  if (!response.ok) {
    throw new Error('Health check failed');
  }
  return response.json();
}

/**
 * Get drift detection service status
 */
export async function getDriftStatus() {
  const response = await fetch(`${API_BASE_URL}/api/v1/drift/status`);
  if (!response.ok) {
    throw new Error('Status check failed');
  }
  return response.json();
}

/**
 * Analyze drift with manual base/head refs (non-streaming)
 */
export async function analyzeDrift(repoUrl, baseRef, headRef, githubToken = null) {
  const response = await fetch(`${API_BASE_URL}/api/v1/drift/analyze`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      repo_url: repoUrl,
      base_ref: baseRef,
      head_ref: headRef,
      github_token: githubToken,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error_message || 'Analysis failed');
  }

  return response.json();
}

/**
 * Scan repository with auto-detection (SSE streaming)
 * Just paste a repo URL - backend auto-detects latest commit
 */
export function scanRepository(repoUrl, githubToken = null, onEvent) {
  // Use fetch with SSE since EventSource doesn't support POST
  return createSSEConnection(`${API_BASE_URL}/api/v1/drift/scan`, {
    repo_url: repoUrl,
    github_token: githubToken,
  }, onEvent);
}

/**
 * Scan pull request (SSE streaming)
 */
export function scanPullRequest(repoUrl, prNumber = null, githubToken = null, onEvent) {
  return createSSEConnection(`${API_BASE_URL}/api/v1/drift/scan/pr`, {
    repo_url: repoUrl,
    pr_number: prNumber,
    github_token: githubToken,
  }, onEvent);
}

/**
 * Analyze drift with streaming (manual refs)
 */
export function analyzeDriftStream(repoUrl, baseRef, headRef, githubToken = null, onEvent) {
  return createSSEConnection(`${API_BASE_URL}/api/v1/drift/analyze/stream`, {
    repo_url: repoUrl,
    base_ref: baseRef,
    head_ref: headRef,
    github_token: githubToken,
  }, onEvent);
}

/**
 * Create SSE connection using fetch (since EventSource doesn't support POST)
 */
async function createSSEConnection(url, body, onEvent) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // Return controller to allow cancellation
  const controller = {
    cancel: () => reader.cancel(),
  };

  // Process stream
  (async () => {
    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let currentEvent = null;
        let currentData = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            currentData = line.slice(5).trim();
          } else if (line === '' && currentEvent && currentData) {
            // Complete event received
            try {
              const data = JSON.parse(currentData);
              onEvent(currentEvent, data);
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
            currentEvent = null;
            currentData = '';
          }
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('SSE stream error:', error);
        onEvent('error', { error_message: error.message });
      }
    }
  })();

  return controller;
}

/**
 * Parse severity color
 */
export function getSeverityColor(severity) {
  const colors = {
    CRITICAL: '#dc2626',
    HIGH: '#f59e0b',
    MEDIUM: '#3b82f6',
    LOW: '#10b981',
  };
  return colors[severity] || '#6b7280';
}

/**
 * Parse severity badge style
 */
export function getSeverityBadgeClass(severity) {
  const classes = {
    CRITICAL: 'severity-critical',
    HIGH: 'severity-high',
    MEDIUM: 'severity-medium',
    LOW: 'severity-low',
  };
  return classes[severity] || 'severity-low';
}

/**
 * Format timestamp
 */
export function formatTimestamp(isoString) {
  const date = new Date(isoString);
  return date.toLocaleString();
}

/**
 * Format duration
 */
export function formatDuration(ms) {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const seconds = (ms / 1000).toFixed(1);
  return `${seconds}s`;
}

// Made with Bob
